/**
 * useMainLogbook.js
 *
 * Domain hook for the Main Logbook - Anchor Mode v2.0 compliant
 * The Main Logbook is the official vessel record, receiving entries from all modules
 *
 * This hook RECEIVES entries from:
 * - Voyages (Let Go / All Fast auto-log)
 * - Drills (completion auto-log)
 * - Bunkering (both simple and full auto-log)
 * - Defects (when raised)
 * - Emergencies (when activated)
 * - Crew changes (sign on/off)
 * - Critical maintenance (when completed)
 * - Manual entries (user-created)
 *
 * Base key: 'operations_main_logbook' (vessel scoping handled by useStorageState)
 *
 * @example
 * const {
 *   getEntriesForDate,
 *   addAutoEntry,
 *   addManualEntry,
 *   exportLogbook
 * } = useMainLogbook();
 */

import { useCallback, useMemo } from 'react';
import { useStorageState } from '../../useStorageState';
import { useVessel } from '../../../contexts/VesselContext';
import { usePermissions } from '../../usePermissions';
import { getCreatedByName } from '../../../components/tailwind/shared/AttributionBadge';
import { createAuditEntry, appendToAuditLog } from '../../../utils/auditUtils';
import {
  LOGBOOK_ENTRY_TYPES,
  LOGBOOK_ENTRY_TYPE_LABELS,
  LOGBOOK_ENTRY_TYPE_ICONS,
  MANUAL_ENTRY_CATEGORIES,
  OPERATIONS_STORAGE_KEYS,
  EXPORT_FORMATS
} from '../../../config/operationsConfig';

// RBAC multi-signature shim
const canDo = (can, resource, action) => {
  try {
    if (!can || typeof can !== 'function') return false;
    // Try usePermissions format first: can(action, resource)
    return (
      can(action, resource) ||
      (can.length >= 2 && can(resource, action)) ||
      can(`${resource}:${action}`) ||
      can({ resource, action })
    );
  } catch {
    return false;
  }
};

/**
 * Generate unique ID
 */
const generateId = (prefix) => {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

/**
 * Get start and end of day for a date
 */
const getDayBounds = (date) => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);

  const end = new Date(date);
  end.setHours(23, 59, 59, 999);

  return { start, end };
};

/**
 * Format date as ISO date string (YYYY-MM-DD)
 */
const formatDateKey = (date) => {
  const d = new Date(date);
  return d.toISOString().split('T')[0];
};

export const useMainLogbook = () => {
  const { getCurrentVessel } = useVessel();
  const currentVessel = getCurrentVessel();
  const { can, currentUser } = usePermissions();

  // Base key only - vessel scoping handled by useStorageState
  const { value: entries = [], setValue: setEntries } = useStorageState(
    OPERATIONS_STORAGE_KEYS.MAIN_LOGBOOK,
    []
  );

  // ============================================================================
  // QUERY METHODS
  // ============================================================================

  /**
   * Get entries for a specific date
   */
  const getEntriesForDate = useCallback((date) => {
    const { start, end } = getDayBounds(date);

    return entries
      .filter(entry => {
        const entryDate = new Date(entry.timestamp);
        return entryDate >= start && entryDate <= end;
      })
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [entries]);

  /**
   * Get unsigned (unlocked) entries for a specific date
   * Used for Sign Off Day modal to show what will be locked
   */
  const getUnsignedEntriesForDate = useCallback((date) => {
    const { start, end } = getDayBounds(date);

    return entries
      .filter(entry => {
        const entryDate = new Date(entry.timestamp);
        return entryDate >= start && entryDate <= end && !entry.isLocked;
      })
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [entries]);

  /**
   * Get entries for a date range
   */
  const getEntriesForRange = useCallback((startDate, endDate) => {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);

    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);

    return entries
      .filter(entry => {
        const entryDate = new Date(entry.timestamp);
        return entryDate >= start && entryDate <= end;
      })
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [entries]);

  /**
   * Get entries by type
   */
  const getEntriesByType = useCallback((type, limit = 50) => {
    return entries
      .filter(entry => entry.type === type)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
      .slice(0, limit);
  }, [entries]);

  /**
   * Search entries
   */
  const searchEntries = useCallback((query, filters = {}) => {
    const searchTerm = query.toLowerCase().trim();

    return entries
      .filter(entry => {
        // Type filter
        if (filters.types && filters.types.length > 0) {
          if (!filters.types.includes(entry.type)) return false;
        }

        // Date range filter
        if (filters.startDate) {
          const entryDate = new Date(entry.timestamp);
          const start = new Date(filters.startDate);
          start.setHours(0, 0, 0, 0);
          if (entryDate < start) return false;
        }

        if (filters.endDate) {
          const entryDate = new Date(entry.timestamp);
          const end = new Date(filters.endDate);
          end.setHours(23, 59, 59, 999);
          if (entryDate > end) return false;
        }

        // Search term
        if (searchTerm) {
          const title = (entry.title || '').toLowerCase();
          const summary = (entry.summary || '').toLowerCase();
          const notes = (entry.data?.notes || '').toLowerCase();
          const createdBy = (getCreatedByName(entry.createdBy) || '').toLowerCase();

          if (!title.includes(searchTerm) &&
              !summary.includes(searchTerm) &&
              !notes.includes(searchTerm) &&
              !createdBy.includes(searchTerm)) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [entries]);

  /**
   * Get entry by ID
   */
  const getEntry = useCallback((entryId) => {
    return entries.find(e => e.id === entryId) || null;
  }, [entries]);

  /**
   * Get today's entries
   */
  const getTodayEntries = useMemo(() => {
    return getEntriesForDate(new Date());
  }, [getEntriesForDate]);

  // ============================================================================
  // ADD ENTRY METHODS
  // ============================================================================

  /**
   * Add an auto-generated entry from another module
   * This is called by other modules (Drills, Defects, etc.) to log to the Main Logbook
   */
  const addAutoEntry = useCallback(async (entryData) => {
    // Auto entries don't require specific permission check - they come from trusted sources
    const now = new Date().toISOString();

    const newEntry = {
      id: generateId('log'),
      type: entryData.type,
      timestamp: entryData.timestamp || now,
      title: entryData.title || LOGBOOK_ENTRY_TYPE_LABELS[entryData.type] || 'Entry',
      summary: entryData.summary || '',
      data: entryData.data || {},
      sourceModule: entryData.sourceModule || 'unknown',
      sourceId: entryData.sourceId || null,
      createdAt: now,
      createdBy: entryData.createdBy || currentUser?.name || 'System',
      isAuto: true
    };

    const updated = [...entries, newEntry];
    await setEntries(updated);

    return newEntry;
  }, [entries, setEntries, currentUser]);

  /**
   * Add a manual entry (user-created)
   */
  const addManualEntry = useCallback(async (entryData) => {
    if (!canDo(can, 'log_entries', 'create')) {
      console.warn('[useMainLogbook] Permission denied: cannot create logbook entry');
      return null;
    }

    const now = new Date().toISOString();

    const newEntry = {
      id: generateId('log'),
      type: LOGBOOK_ENTRY_TYPES.MANUAL,
      timestamp: entryData.timestamp || now,
      title: entryData.title || 'Manual Entry',
      summary: entryData.summary || entryData.content || '',
      category: entryData.category || MANUAL_ENTRY_CATEGORIES.GENERAL,
      data: {
        content: entryData.content || '',
        notes: entryData.notes || ''
      },
      createdAt: now,
      createdBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
      isAuto: false
    };

    const updated = [...entries, newEntry];
    await setEntries(updated);

    return newEntry;
  }, [can, entries, setEntries, currentUser]);

  /**
   * Edit a manual entry (only non-auto entries can be edited)
   */
  const editEntry = useCallback(async (entryId, updates) => {
    if (!canDo(can, 'log_entries', 'update')) {
      return false;
    }

    const entry = entries.find(e => e.id === entryId);
    if (!entry || entry.isAuto) {
      console.warn('[useMainLogbook] Cannot edit auto-generated entries');
      return false;
    }

    // Check for locked entries - IMMUTABILITY ENFORCEMENT (MCA MSN 1905 compliance)
    const entryToEdit = entries.find(e => e.id === entryId);
    if (entryToEdit?.isLocked) {
      console.warn(`[useMainLogbook] Cannot edit locked entry ${entryId} - signed and immutable per MCA regulations`);
      return false;
    }

    // FT-06c: Create audit entry for field changes
    const existingEntry = entries.find(e => e.id === entryId);
    const auditEntry = createAuditEntry(
      existingEntry,
      { ...existingEntry, ...updates },
      currentUser,
      { fieldsToTrack: ['title', 'summary', 'data', 'timestamp', 'type'] }
    );

    const updated = entries.map(e =>
      e.id === entryId
        ? {
            ...e,
            ...updates,
            updatedAt: new Date().toISOString(),
            updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
            auditLog: appendToAuditLog(e.auditLog, auditEntry)
          }
        : e
    );

    await setEntries(updated);
    return true;
  }, [can, entries, setEntries, currentUser]);

  /**
   * Delete a manual entry (only non-auto entries can be deleted)
   */
  const deleteEntry = useCallback(async (entryId) => {
    if (!canDo(can, 'log_entries', 'delete')) {
      return false;
    }

    const entry = entries.find(e => e.id === entryId);
    if (!entry || entry.isAuto) {
      console.warn('[useMainLogbook] Cannot delete auto-generated entries');
      return false;
    }

    // Check for locked entries - IMMUTABILITY ENFORCEMENT (MCA MSN 1905 compliance)
    const entryToDelete = entries.find(e => e.id === entryId);
    if (entryToDelete?.isLocked) {
      console.warn(`[useMainLogbook] Cannot delete locked entry ${entryId} - signed and immutable per MCA regulations`);
      return false;
    }

    const updated = entries.filter(e => e.id !== entryId);
    await setEntries(updated);
    return true;
  }, [can, entries, setEntries]);

  // ============================================================================
  // AUTO-LOG HELPER METHODS
  // These are convenience methods for other modules to call
  // ============================================================================

  /**
   * Log a voyage event (Let Go or All Fast)
   */
  const logVoyageEvent = useCallback(async (voyageData) => {
    const isLetGo = voyageData.eventType === 'let_go';

    return addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.VOYAGE,
      timestamp: voyageData.timestamp,
      title: isLetGo ? 'Let Go' : 'All Fast',
      summary: isLetGo
        ? `Voyage ${voyageData.voyageNumber || 'Unknown'} - ${voyageData.from || 'Unknown'} → ${voyageData.to || 'Unknown'}. Crew: ${voyageData.crewCount || 0}`
        : `Voyage ${voyageData.voyageNumber || 'Unknown'} - ${voyageData.location || 'Unknown'}. Duration: ${voyageData.duration || 'Unknown'}`,
      data: {
        voyageId: voyageData.voyageId,
        voyageNumber: voyageData.voyageNumber,
        eventType: voyageData.eventType,
        from: voyageData.from,
        to: voyageData.to,
        location: voyageData.location,
        crewCount: voyageData.crewCount,
        passengerCount: voyageData.passengerCount,
        duration: voyageData.duration,
        operations: voyageData.operations
      },
      sourceModule: 'voyages',
      sourceId: voyageData.voyageId
    });
  }, [addAutoEntry]);

  /**
   * Log a drill completion
   */
  const logDrill = useCallback(async (drillData) => {
    return addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.DRILL,
      timestamp: drillData.timestamp || drillData.completedAt,
      title: drillData.drillName || drillData.type,
      summary: `${drillData.outcome || 'Completed'}. ${drillData.participantCount || 'All crew'} participated.`,
      data: {
        drillId: drillData.drillId,
        drillType: drillData.type,
        participants: drillData.participants,
        participantCount: drillData.participantCount,
        duration: drillData.duration,
        outcome: drillData.outcome,
        notes: drillData.notes
      },
      sourceModule: 'drills',
      sourceId: drillData.drillId
    });
  }, [addAutoEntry]);

  /**
   * Log a bunkering operation
   */
  const logBunkering = useCallback(async (bunkeringData) => {
    const isSimple = bunkeringData.type === 'simple';

    return addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.BUNKERING,
      timestamp: bunkeringData.timestamp,
      title: isSimple ? 'Fuel Top-Up' : 'Bunkering Operation',
      summary: `Received ${bunkeringData.quantity}L ${bunkeringData.fuelType}${bunkeringData.supplier ? ` from ${bunkeringData.supplier}` : ''}`,
      data: {
        bunkeringId: bunkeringData.bunkeringId,
        bunkeringType: bunkeringData.type,
        fuelType: bunkeringData.fuelType,
        quantity: bunkeringData.quantity,
        supplier: bunkeringData.supplier,
        filledBy: bunkeringData.filledBy,
        spillage: bunkeringData.spillage
      },
      sourceModule: 'bunkering',
      sourceId: bunkeringData.bunkeringId
    });
  }, [addAutoEntry]);

  /**
   * Log a defect being raised
   */
  const logDefect = useCallback(async (defectData) => {
    return addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.DEFECT,
      timestamp: defectData.timestamp || defectData.raisedAt,
      title: 'Defect Raised',
      summary: `${defectData.title || defectData.description}. Priority: ${defectData.priority || 'Normal'}`,
      data: {
        defectId: defectData.defectId,
        title: defectData.title,
        description: defectData.description,
        priority: defectData.priority,
        category: defectData.category,
        location: defectData.location
      },
      sourceModule: 'defects',
      sourceId: defectData.defectId
    });
  }, [addAutoEntry]);

  /**
   * Log an emergency event
   */
  const logEmergency = useCallback(async (emergencyData) => {
    return addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.EMERGENCY,
      timestamp: emergencyData.timestamp,
      title: `${emergencyData.mode === 'drill' ? 'Drill: ' : 'EMERGENCY: '}${emergencyData.emergencyType}`,
      summary: emergencyData.summary || `${emergencyData.emergencyType} ${emergencyData.mode === 'drill' ? 'drill' : 'response'} ${emergencyData.action || 'activated'}`,
      data: {
        emergencyId: emergencyData.emergencyId,
        emergencyType: emergencyData.emergencyType,
        mode: emergencyData.mode, // 'real' or 'drill'
        action: emergencyData.action, // 'started', 'completed', etc.
        position: emergencyData.position,
        notes: emergencyData.notes
      },
      sourceModule: 'emergency',
      sourceId: emergencyData.emergencyId
    });
  }, [addAutoEntry]);

  /**
   * Log a crew change
   */
  const logCrewChange = useCallback(async (crewData) => {
    const signOns = crewData.signOns || [];
    const signOffs = crewData.signOffs || [];

    let summary = '';
    if (signOns.length > 0) {
      summary += `Signed on: ${signOns.map(c => c.name).join(', ')}. `;
    }
    if (signOffs.length > 0) {
      summary += `Signed off: ${signOffs.map(c => c.name).join(', ')}.`;
    }

    return addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.CREW_CHANGE,
      timestamp: crewData.timestamp,
      title: 'Crew Change',
      summary: summary.trim(),
      data: {
        signOns,
        signOffs,
        notes: crewData.notes
      },
      sourceModule: 'crew',
      sourceId: crewData.changeId
    });
  }, [addAutoEntry]);

  /**
   * Log critical maintenance completion
   */
  const logMaintenance = useCallback(async (maintenanceData) => {
    return addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.MAINTENANCE,
      timestamp: maintenanceData.timestamp || maintenanceData.completedAt,
      title: 'Maintenance Completed',
      summary: `${maintenanceData.jobName || maintenanceData.title}. ${maintenanceData.equipment || ''}`,
      data: {
        jobId: maintenanceData.jobId,
        jobName: maintenanceData.jobName,
        equipment: maintenanceData.equipment,
        completedBy: maintenanceData.completedBy,
        notes: maintenanceData.notes
      },
      sourceModule: 'maintenance',
      sourceId: maintenanceData.jobId
    });
  }, [addAutoEntry]);

  /**
   * Log a defect being resolved/closed
   */
  const logDefectResolved = useCallback(async (defectData) => {
    return addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED,
      timestamp: defectData.timestamp || defectData.resolvedAt,
      title: 'Defect Resolved',
      summary: `${defectData.title || defectData.description}. Resolution: ${defectData.resolution || 'Completed'}`,
      data: {
        defectId: defectData.defectId,
        title: defectData.title,
        description: defectData.description,
        resolution: defectData.resolution,
        resolvedBy: defectData.resolvedBy,
        priority: defectData.priority,
        daysOpen: defectData.daysOpen
      },
      sourceModule: 'defects',
      sourceId: defectData.defectId,
      createdBy: defectData.resolvedBy
    });
  }, [addAutoEntry]);

  /**
   * Log an incident
   */
  const logIncident = useCallback(async (incidentData) => {
    return addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.INCIDENT,
      timestamp: incidentData.timestamp || incidentData.occurredAt,
      title: incidentData.type || 'Incident',
      summary: incidentData.summary || incidentData.description,
      data: {
        incidentId: incidentData.incidentId,
        type: incidentData.type,
        description: incidentData.description,
        severity: incidentData.severity,
        location: incidentData.location,
        personnel: incidentData.personnel,
        immediateActions: incidentData.immediateActions,
        reportedBy: incidentData.reportedBy
      },
      sourceModule: 'incidents',
      sourceId: incidentData.incidentId,
      createdBy: incidentData.reportedBy
    });
  }, [addAutoEntry]);

  /**
   * Log a familiarisation completion
   */
  const logFamiliarisation = useCallback(async (famData) => {
    return addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.FAMILIARISATION,
      timestamp: famData.timestamp || famData.completedAt,
      title: 'Crew Familiarisation',
      summary: `${famData.crewName} completed ${famData.checklistName || 'familiarisation'}`,
      data: {
        crewId: famData.crewId,
        crewName: famData.crewName,
        role: famData.role,
        checklistId: famData.checklistId,
        checklistName: famData.checklistName,
        itemsCompleted: famData.itemsCompleted,
        completedBy: famData.completedBy
      },
      sourceModule: 'crew',
      sourceId: famData.familiarisationId || famData.crewId,
      createdBy: famData.completedBy
    });
  }, [addAutoEntry]);

  /**
   * Log an hours of rest violation
   */
  const logHoursViolation = useCallback(async (violationData) => {
    const result = await addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.HOR_VIOLATION,
      timestamp: violationData.timestamp || violationData.occurredAt,
      title: 'Hours of Rest Violation',
      summary: `${violationData.crewName}: ${violationData.violationType}. Reason: ${violationData.reason || 'Not specified'}`,
      data: {
        crewId: violationData.crewId,
        crewName: violationData.crewName,
        violationType: violationData.violationType,
        reason: violationData.reason,
        date: violationData.date,
        hoursWorked: violationData.hoursWorked,
        restPeriod: violationData.restPeriod
      },
      sourceModule: 'crew',
      sourceId: violationData.violationId || violationData.crewId,
      createdBy: violationData.recordedBy
    });

    // Send immediate email notification for HOR violation
    // Fire-and-forget pattern - notification failures don't affect logging
    if (typeof window !== 'undefined') {
      // Dynamically import to avoid circular dependencies
      import('../../useNotifications').then(({ useNotifications }) => {
        // Can't use hook here - use fetch directly
        const session = window.__seaready_session; // Set by AuthProvider
        if (session?.token) {
          fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-notification`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${session.token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              type: 'HOR_VIOLATION',
              data: {
                ...violationData,
                vesselName: window.__seaready_vessel?.name || 'Vessel'
              },
              recipients: window.__seaready_notification_recipients || []
            })
          }).catch(err => {
            console.warn('[HOR Violation] Failed to send notification:', err);
          });
        }
      }).catch(() => {
        // Ignore import errors - notifications are optional
      });
    }

    return result;
  }, [addAutoEntry]);

  /**
   * Log a permit being issued (hot work, confined space, etc.)
   */
  const logPermitIssued = useCallback(async (permitData) => {
    return addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.PERMIT,
      timestamp: permitData.timestamp || permitData.issuedAt,
      title: `Permit Issued: ${permitData.permitType}`,
      summary: `${permitData.workDescription}. Location: ${permitData.location || 'N/A'}`,
      data: {
        permitId: permitData.permitId,
        permitType: permitData.permitType,
        workDescription: permitData.workDescription,
        location: permitData.location,
        issuedBy: permitData.issuedBy,
        validFrom: permitData.validFrom,
        validUntil: permitData.validUntil,
        precautions: permitData.precautions,
        status: 'issued'
      },
      sourceModule: 'permits',
      sourceId: permitData.permitId,
      createdBy: permitData.issuedBy
    });
  }, [addAutoEntry]);

  /**
   * Log a permit being closed
   */
  const logPermitClosed = useCallback(async (permitData) => {
    return addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.PERMIT,
      timestamp: permitData.timestamp || permitData.closedAt,
      title: `Permit Closed: ${permitData.permitType}`,
      summary: `Work completed. ${permitData.notes || ''}`.trim(),
      data: {
        permitId: permitData.permitId,
        permitType: permitData.permitType,
        workDescription: permitData.workDescription,
        closedBy: permitData.closedBy,
        status: 'closed',
        notes: permitData.notes
      },
      sourceModule: 'permits',
      sourceId: permitData.permitId,
      createdBy: permitData.closedBy
    });
  }, [addAutoEntry]);

  /**
   * Log checklist completion to Main Logbook
   * Called when operational checklists are completed
   * FT-123: Fixed to properly handle completedBy as object and use currentUser fallback
   */
  const logChecklistCompletion = useCallback(async (checklistData) => {
    // FT-123: Normalize completedBy to always be an object with name, id, role
    // If string passed, convert to object. If nothing passed, use currentUser.
    let completedBy = checklistData.completedBy;
    if (typeof completedBy === 'string') {
      completedBy = { name: completedBy, role: 'Crew' };
    } else if (!completedBy || !completedBy.name) {
      completedBy = {
        userId: currentUser?.id,
        name: currentUser?.name || 'Unknown',
        role: currentUser?.role || 'Crew'
      };
    }

    const completedByName = completedBy.name || 'Unknown';

    return addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.CHECKLIST,
      title: checklistData.checklistName || 'Checklist Completed',
      summary: `${checklistData.checklistName || 'Checklist'} completed by ${completedByName}`,
      sourceModule: 'checklists',
      sourceId: checklistData.id,
      data: {
        checklistId: checklistData.checklistId,
        checklistName: checklistData.checklistName,
        checklistType: checklistData.checklistType,
        itemsCompleted: checklistData.checks?.length || 0,
        itemsPassed: checklistData.checks?.filter(c => c.passed)?.length || 0,
        linkedContext: checklistData.linkedContext || null,
        participants: checklistData.participants || [],
        scenario: checklistData.scenario || '',
        notes: checklistData.notes || '',
        checks: checklistData.checks || []  // FT-123: Include check items for detail view
      },
      createdBy: completedBy
    });
  }, [addAutoEntry, currentUser]);

  /**
   * FT-87: Log a daily check completion
   * Called when a daily check category is completed
   * FT-123: Fixed to properly handle completedBy as object and use currentUser fallback
   */
  const logDailyCheck = useCallback(async (dailyCheckData) => {
    // FT-123: Normalize completedBy to always be an object with name, id, role
    // If string passed, convert to object. If nothing passed, use currentUser.
    let completedBy = dailyCheckData.completedBy;
    if (typeof completedBy === 'string') {
      completedBy = { name: completedBy, role: 'Crew' };
    } else if (!completedBy || !completedBy.name) {
      completedBy = {
        userId: currentUser?.id,
        name: currentUser?.name || 'Unknown',
        role: currentUser?.role || 'Crew'
      };
    }

    const completedByName = completedBy.name || 'Unknown';

    return addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.DAILY_CHECK,
      timestamp: dailyCheckData.completedAt,
      title: `Daily Check: ${dailyCheckData.categoryLabel || dailyCheckData.category}`,
      summary: `${dailyCheckData.categoryLabel || dailyCheckData.category} completed by ${completedByName}`,
      data: {
        category: dailyCheckData.category,
        categoryLabel: dailyCheckData.categoryLabel,
        checksCount: dailyCheckData.checksCount || 0,
        checksPassed: dailyCheckData.checksPassed || 0,
        checks: dailyCheckData.checks || [],
        notes: dailyCheckData.notes || ''   // FT-309: thread general notes through to persist for logbook display
      },
      sourceModule: 'daily_checks',
      sourceId: `daily-check-${dailyCheckData.category}-${dailyCheckData.completedAt}`,
      createdBy: completedBy
    });
  }, [addAutoEntry, currentUser]);

  /**
   * FT-107: Log a compliance override
   * Called when a pre-departure compliance blocker is overridden with reason
   */
  const logComplianceOverride = useCallback(async (overrideData) => {
    return addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.COMPLIANCE_OVERRIDE,
      timestamp: overrideData.timestamp || new Date().toISOString(),
      title: 'Compliance Override',
      summary: `${overrideData.blockerName} overridden${overrideData.crewName ? ` for ${overrideData.crewName}` : ''}. Reason: ${overrideData.reason?.substring(0, 100)}${overrideData.reason?.length > 100 ? '...' : ''}`,
      data: {
        voyageId: overrideData.voyageId,
        voyageNumber: overrideData.voyageNumber,
        blockerType: overrideData.blockerType,          // 'baseline' | 'collective'
        blockerKey: overrideData.blockerKey,            // e.g., 'medical_fitness', 'radio_operator'
        blockerName: overrideData.blockerName,          // Human-readable name
        crewId: overrideData.crewId || null,
        crewName: overrideData.crewName || null,
        reason: overrideData.reason,                    // Required free text
        dispensationRef: overrideData.dispensationRef || null,  // Optional
        acknowledgedBy: overrideData.acknowledgedBy,
        wbc3Ref: overrideData.wbc3Ref || null
      },
      sourceModule: 'voyages',
      sourceId: overrideData.voyageId || overrideData.overrideId,
      createdBy: overrideData.acknowledgedBy
    });
  }, [addAutoEntry]);

  /**
   * Sign an entry (Master's sign-off for regulatory compliance)
   */
  const signEntry = useCallback(async (entryId, signature) => {
    // Signing requires Master permission
    if (!canDo(can, 'log_entries', 'sign')) {
      console.warn('[useMainLogbook] Permission denied: cannot sign logbook entry');
      return false;
    }

    const entry = entries.find(e => e.id === entryId);
    if (!entry) {
      console.warn('[useMainLogbook] Entry not found:', entryId);
      return false;
    }

    const updated = entries.map(e =>
      e.id === entryId
        ? {
            ...e,
            signedAt: new Date().toISOString(),
            signedBy: signature.signedBy || currentUser?.name || 'Master',
            signatureType: signature.type || 'master_approval',
            signatureNotes: signature.notes || '',
            // IMMUTABILITY FIELDS - MCA MSN 1905 COMPLIANCE
            isLocked: true,
            lockedAt: new Date().toISOString(),
            lockedBy: signature.signedBy || currentUser?.name || 'Master'
          }
        : e
    );

    await setEntries(updated);

    // Log the sign-off action itself
    await addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.SIGN_OFF,
      timestamp: new Date().toISOString(),
      title: 'Entry Signed',
      summary: `${entry.title} signed by ${signature.signedBy || currentUser?.name || 'Master'}`,
      data: {
        signedEntryId: entryId,
        signedEntryTitle: entry.title,
        signedEntryType: entry.type,
        signedBy: signature.signedBy || currentUser?.name,
        signatureType: signature.type || 'master_approval',
        notes: signature.notes
      },
      sourceModule: 'logbook',
      sourceId: entryId
    });

    return true;
  }, [can, entries, setEntries, currentUser, addAutoEntry]);

  /**
   * Sign off all unsigned entries for a specific date (Captain's daily sign-off)
   * This is the correct maritime workflow - batch sign at end of day
   * @param {Date|string} date - The date to sign off
   * @param {string} signatureImage - Base64 PNG of drawn signature
   * @param {string} signerName - Name of the captain/signer
   * @returns {Promise<{success: boolean, count: number}>}
   */
  const signOffDay = useCallback(async (date, signatureImage, signerName) => {
    // Signing requires Master permission
    if (!canDo(can, 'log_entries', 'sign')) {
      console.warn('[useMainLogbook] Permission denied: cannot sign logbook entries');
      return { success: false, count: 0 };
    }

    const { start, end } = getDayBounds(date);
    const now = new Date().toISOString();

    // Find all unsigned entries for this date
    const unsignedEntries = entries.filter(entry => {
      const entryDate = new Date(entry.timestamp);
      return entryDate >= start && entryDate <= end && !entry.isLocked;
    });

    if (unsignedEntries.length === 0) {
      console.warn('[useMainLogbook] No unsigned entries for this date');
      return { success: false, count: 0 };
    }

    // Batch update: lock all unsigned entries with signature
    const unsignedIds = new Set(unsignedEntries.map(e => e.id));
    const updated = entries.map(entry => {
      if (unsignedIds.has(entry.id)) {
        return {
          ...entry,
          isLocked: true,
          lockedAt: now,
          lockedBy: signerName || currentUser?.name || 'Master',
          signatureImage: signatureImage
        };
      }
      return entry;
    });

    await setEntries(updated);

    // Log the sign-off action
    // FT-84: Use end-of-day timestamp for filtering (so entry appears on correct day in logbook)
    // but also store actualSignedAt for display purposes (shows real signing time)
    const signOffTimestamp = end.toISOString();
    const actualSignedAt = new Date().toISOString();  // FT-84: Real signing time for display
    await addAutoEntry({
      type: LOGBOOK_ENTRY_TYPES.SIGN_OFF,
      timestamp: signOffTimestamp,
      title: 'Daily Sign-Off',
      summary: `${unsignedEntries.length} entries signed by ${signerName || currentUser?.name || 'Master'}`,
      data: {
        signedDate: formatDateKey(date),
        actualSignedAt: actualSignedAt,  // FT-84: Actual signing time for display
        entriesCount: unsignedEntries.length,
        signedEntryIds: Array.from(unsignedIds),
        signedBy: signerName || currentUser?.name,
        signatureImage: signatureImage
      },
      sourceModule: 'logbook',
      sourceId: `signoff-${formatDateKey(date)}-${Date.now()}`
    });

    return { success: true, count: unsignedEntries.length };
  }, [can, entries, setEntries, currentUser, addAutoEntry]);

  // ============================================================================
  // EXPORT METHODS
  // ============================================================================

  /**
   * Export logbook data
   */
  const exportLogbook = useCallback(async (format, dateRange, options = {}) => {
    if (!canDo(can, 'log_entries', 'read')) {
      return null;
    }

    // Get entries for the date range
    const exportEntries = getEntriesForRange(dateRange.start, dateRange.end);

    // Filter by types if specified
    let filteredEntries = exportEntries;
    if (options.includeTypes && options.includeTypes.length > 0) {
      filteredEntries = exportEntries.filter(e => options.includeTypes.includes(e.type));
    }

    const vesselName = currentVessel?.name || 'Vessel';
    const startStr = formatDateKey(dateRange.start);
    const endStr = formatDateKey(dateRange.end);

    switch (format) {
      case EXPORT_FORMATS.PDF:
        return {
          format: 'pdf',
          filename: `${vesselName}_Logbook_${startStr}_to_${endStr}.pdf`,
          data: filteredEntries,
          metadata: {
            vessel: vesselName,
            period: `${startStr} to ${endStr}`,
            generatedAt: new Date().toISOString(),
            generatedBy: currentUser?.name || 'Unknown',
            entryCount: filteredEntries.length
          }
        };

      case EXPORT_FORMATS.CSV:
        const csvRows = [
          ['Date', 'Time', 'Type', 'Title', 'Summary', 'Created By'].join(','),
          ...filteredEntries.map(entry => {
            const date = new Date(entry.timestamp);
            return [
              date.toLocaleDateString(),
              date.toLocaleTimeString(),
              LOGBOOK_ENTRY_TYPE_LABELS[entry.type] || entry.type,
              `"${(entry.title || '').replace(/"/g, '""')}"`,
              `"${(entry.summary || '').replace(/"/g, '""')}"`,
              entry.createdBy || ''
            ].join(',');
          })
        ];

        return {
          format: 'csv',
          filename: `${vesselName}_Logbook_${startStr}_to_${endStr}.csv`,
          data: csvRows.join('\n'),
          metadata: {
            vessel: vesselName,
            period: `${startStr} to ${endStr}`,
            generatedAt: new Date().toISOString(),
            entryCount: filteredEntries.length
          }
        };

      case EXPORT_FORMATS.EMAIL:
        const emailBody = filteredEntries
          .map(entry => {
            const date = new Date(entry.timestamp);
            return `${date.toLocaleDateString()} ${date.toLocaleTimeString()} - ${entry.title || LOGBOOK_ENTRY_TYPE_LABELS[entry.type]}\n${entry.summary || ''}`;
          })
          .join('\n\n');

        return {
          format: 'email',
          subject: `${vesselName} Logbook Summary - ${startStr} to ${endStr}`,
          body: `Logbook Summary for ${vesselName}\nPeriod: ${startStr} to ${endStr}\n\n${emailBody}`,
          metadata: {
            vessel: vesselName,
            period: `${startStr} to ${endStr}`,
            generatedAt: new Date().toISOString(),
            entryCount: filteredEntries.length
          }
        };

      default:
        console.warn('[useMainLogbook] Unknown export format:', format);
        return null;
    }
  }, [can, getEntriesForRange, currentVessel, currentUser]);

  // ============================================================================
  // STATISTICS
  // ============================================================================

  /**
   * Get logbook statistics
   */
  const getStats = useCallback(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const recentEntries = entries.filter(e => new Date(e.timestamp) >= thirtyDaysAgo);

    // Count by type
    const byType = {};
    recentEntries.forEach(entry => {
      byType[entry.type] = (byType[entry.type] || 0) + 1;
    });

    return {
      totalEntries: entries.length,
      last30Days: recentEntries.length,
      todayCount: getTodayEntries.length,
      byType
    };
  }, [entries, getTodayEntries]);

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // Data
    entries,
    todayEntries: getTodayEntries,

    // Queries
    getEntriesForDate,
    getUnsignedEntriesForDate,
    getEntriesForRange,
    getEntriesByType,
    searchEntries,
    getEntry,

    // Manual entries
    addManualEntry,
    editEntry,
    deleteEntry,

    // Auto-logging (for other modules to call)
    addAutoEntry,
    logVoyageEvent,
    logDrill,
    logBunkering,
    logDefect,
    logDefectResolved,
    logEmergency,
    logCrewChange,
    logMaintenance,
    logIncident,
    logFamiliarisation,
    logHoursViolation,
    logPermitIssued,
    logPermitClosed,
    logChecklistCompletion,
    logDailyCheck,
    logComplianceOverride,  // FT-107

    // Signing (Master sign-off)
    signEntry,
    signOffDay,

    // Export
    exportLogbook,

    // Statistics
    getStats,

    // Constants
    LOGBOOK_ENTRY_TYPES,
    LOGBOOK_ENTRY_TYPE_LABELS,
    LOGBOOK_ENTRY_TYPE_ICONS,
    MANUAL_ENTRY_CATEGORIES,
    EXPORT_FORMATS
  };
};

export default useMainLogbook;
